import {
  aiTiktokVideoURL,
  aiUgcVideoURL,
  aiVideoEditURL,
  avatarVideoProURL,
  facebookVideoAdURL,
  productAvatarURL,
  videoGenerateURL,
  videoTranslateURL,
  voiceCloneURL,
} from '@/utils/routeConstants';

import { SEOHeadProps } from '../components/SEOHead';
import { FAQItem } from '../components/templates/FAQSection';
import { FeatureItem } from '../components/templates/FeaturesSection';
import { StepData } from '../components/templates/HowToSection';
import { ToolItem } from '../components/templates/TikTokHeroSupportingSection';
import { ReasonItem } from '../components/templates/WhyChooseSection';
import { ScaleTabData } from '../components/UGCGenerator/UGCScaleSection';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createHowToSchema,
  createSoftwareAppSchema,
  createWebPageSchema,
} from '../utils/structuredData';

const PAGE_URL = 'https://falcocut.ai/ai-reel-generator';
const PAGE_TITLE = 'AI Reel Generator - Make High‑Impact Reels Effortlessly';
const PAGE_DESCRIPTION =
  "Make scroll‑stopping reels instantly with FalcoCut's AI reel generator. Convert your ideas into engaging short‑form videos optimized for Instagram and social platforms, featuring AI‑powered narration, visuals, and captions.";

/** 首屏卡片图片配置 */
export const HERO_CARD_IMAGES: Record<string, { imageUrl: string; alt: string }> = {
  'ugc-video-ads': {
    imageUrl: 'https://assets.falcocut.ai/ai-reel-generator/ugc-video-ads.png',
    alt: 'UGC Video Ads',
  },
  'video-from-product-image': {
    imageUrl: 'https://assets.falcocut.ai/ai-reel-generator/video-from-product-image.png',
    alt: 'Video from Product Image',
  },
  'generate-image': {
    imageUrl: 'https://assets.falcocut.ai/ai-reel-generator/generate-image.png',
    alt: 'Generate Image',
  },
  'video-from-avatar-image': {
    imageUrl: 'https://assets.falcocut.ai/ai-reel-generator/video-from-avatar-image.png',
    alt: 'Video from Avatar Image',
  },
};

/** Hero Section 配置 */
export const HERO_CONFIG = {
  title: 'AI Reel Generator',
  subtitle:
    "Share product highlights, jump into trending moments, or express your ideas across today's short‑form platforms. Generate polished, attention‑grabbing reels with FalcoCut's AI reel generator—fast, flexible, and ready for every creator.",
};

/** Hero Supporting Section 配置 */
export const HERO_SUPPORTING_CONFIG = {
  title: 'What Kind of Reels Can FalcoCut Make',
  subtitle:
    'FalcoCut gives you flexible ways to create reels from whatever you have—photos, text ideas, or existing clips. Build short‑form videos quickly with AI that handles the heavy lifting so you can focus on your message.',
  ctaText: 'Create Reels Now',
};

export const HERO_SUPPORTING_TOOLS: ToolItem[] = [
  {
    title: 'Image to Video',
    description:
      'Upload your photos, describe the style you want, and watch them transform into dynamic bite‑sized videos ready for Instagram, TikTok, and Facebook.',
    imageUrl: 'https://assets.falcocut.ai/ai-reel-generator/image-to-video.png',
  },
  {
    title: 'Text to Video',
    description:
      'Enter your script, and the AI automatically generates narration, selects visuals, and adds captions to create a polished, platform‑ready video.',
    imageUrl: 'https://assets.falcocut.ai/ai-reel-generator/text-to-video.png',
  },
  {
    title: 'Video to Video',
    description:
      'Upload a clip and use a simple text prompt to adjust the tone, swap the product, or reshape the entire reel—no manual editing required.',
    imageUrl: 'https://assets.falcocut.ai/ai-reel-generator/video-to-video.png',
  },
];

/** How To Section 配置 */
export const HOW_TO_CONFIG = {
  title: 'How to Create Reels with AI',
  ctaText: 'Create Reels Now',
  imageUrl:
    'https://assets.falcocut.ai/ai-reel-generator/how-to-create-reels-with-ai.png',
};

export const HOW_TO_STEPS: StepData[] = [
  {
    number: 1,
    title: 'Type a Prompt or Upload an Image',
    description:
      'Describe the idea you want to turn into a reel or upload an image as your starting point. FalcoCut interprets your input and prepares the foundation for your video.',
  },
  {
    number: 2,
    title: 'Pick the AI Model',
    description:
      'Choose from Sora, Kling, Seedance, or Veo to define the style and motion of your reel. Each model offers a distinct creative approach tailored to different visual outcomes.',
  },
  {
    number: 3,
    title: 'Define Reel Length and Layout',
    description:
      "Select your preferred duration, resolution, and aspect ratio to match the platform you're targeting. These settings ensure your reel is optimized for quality and viewing experience.",
  },
  {
    number: 4,
    title: 'Download and Post',
    description:
      'Generate your reel, download the finished video, and share it directly on Instagram, Facebook, TikTok, YouTube Shorts, or any platform where short‑form content performs best.',
  },
];

/** Features Section 配置 */
export const FEATURES_CONFIG = {
  title: 'Smart Features for Making Viral Reels',
  subtitle:
    'Transform your marketing strategy by using FalcoCut to generate high-performing short-form videos. Our advanced platform handles everything from visual creation to final editing, ensuring your brand stays relevant across every social feed.',
  ctaText: 'Create AI Reels',
};

export const FEATURES_LIST: FeatureItem[] = [
  {
    title: 'Transform Prompts into Professional Reels',
    description:
      'Turn a simple text idea into a complete video masterpiece without the wait. FalcoCut analyzes your creative vision to generate high-quality cinematic b-roll or narrative scenes that match your brand. You no longer need to spend hours brainstorming shots or organizing complex filming schedules. Simply describe your goal, and the engine produces a viral-ready clip for you. So you can test dozens of different content ideas rapidly. Your video production workflow becomes faster and more efficient than ever before possible.',
    imageUrl:
      'https://assets.falcocut.ai/ai-reel-generator/transform-prompts-into-professional-reels.png',
    imageAlt: 'Transform Prompts into Professional Reels',
    ctaText: 'Create AI Reels',
  },
  {
    title: 'Seamless Product Integration into Any Scene',
    description:
      'Place your physical products naturally into realistic environments without a physical photoshoot. Our AI technology maps your items into lifestyle demos or aesthetic flat-lays. You can display your brand in premium locations without paying for expensive studio rentals. Our image-to-video tool creates an authentic feel that helps customers visualize the product in their lives. These generated scenes maintain consistent lighting and shadows for a perfectly polished look. Your marketing assets will look professional while costing only a fraction of traditional photography.',
    imageUrl:
      'https://assets.falcocut.ai/ai-reel-generator/seamless-product-integration-into-any-scene.png',
    imageAlt: 'Seamless Product Integration into Any Scene',
    ctaText: 'Create AI Reels',
  },
  {
    title: 'Launch Faceless Channels with Realistic Avatars',
    description:
      'Build a massive social following without ever appearing on camera yourself. Choose from our library of hyper-real personas to post daily news updates or educational explainers. These digital creators deliver your message with natural movements and convincing human expressions. It is the perfect solution for shy entrepreneurs or brands wanting a consistent face. You can manage multiple niche accounts simultaneously using different virtual personalities for each. Your presence grows across platforms while you maintain total privacy and creative control.',
    imageUrl:
      'https://assets.falcocut.ai/ai-reel-generator/launch-faceless-channels-with-realistic-avatars.png',
    imageAlt: 'Launch Faceless Channels with Realistic Avatars',
    ctaText: 'Create AI Reels',
  },
  {
    title: 'Smart Captions and Global Voiceover Support',
    description:
      "Expand your international reach by localizing your content into over thirty different languages. Our system automatically generates perfectly timed captions for instructional guides or quick tips to keep viewers engaged on mute. The advanced voice cloning technology ensures your narration sounds natural in every dialect. You can translate your existing videos while keeping the original speaker's unique tone. Now your brand is accessible to a global audience with just one click. Your reels will resonate with local communities through authentic and timely multilingual delivery.",
    imageUrl:
      'https://assets.falcocut.ai/ai-reel-generator/smart-captions-and-global-voiceover-support.png',
    imageAlt: 'Smart Captions and Global Voiceover Support',
    ctaText: 'Create AI Reels',
  },
  {
    title: 'All-in-One AI Video Editing Suite',
    description:
      'Refine your social media content effortlessly using our integrated smart editing tools. You can swap backgrounds on unboxing videos or remove unwanted objects for a clean look. FalcoCut allows you to restyle your footage to match the latest trending formats. The interface is designed for speed, making complex visual effects accessible to everyone. You can fix errors or update product details without starting the project over. Your final exports will always meet the high-quality standards of professional video editors.',
    imageUrl:
      'https://assets.falcocut.ai/ai-reel-generator/all-in-one-ai-video-editing-suite.png',
    imageAlt: 'All-in-One AI Video Editing Suite',
    ctaText: 'Create AI Reels',
  },
  {
    title: 'Optimize Content for Every Social Platform',
    description:
      'Tailor your videos perfectly for TikTok trends, Instagram Reels, and YouTube Shorts instantly. Our reel generator automatically adjusts the framing and duration to fit each specific algorithm. You can reach diverse audiences by distributing high-quality clips across all major channels. This multi-platform approach ensures your brand message stays consistent and highly visible everywhere. Stop worrying about technical requirements and focus on sharing your unique brand story. Your video strategy will benefit from a wider reach and much higher engagement.',
    imageUrl:
      'https://assets.falcocut.ai/ai-reel-generator/optimize-content-for-every-social-platform.png',
    imageAlt: 'Optimize Content for Every Social Platform',
    ctaText: 'Create AI Reels',
  },
];

/** Video Showcase Section 配置 */
export const EXAMPLE_VIDEOS_CONFIG = {
  title: 'The Creative Engine Behind Your Reels',
  subtitle:
    'FalcoCut serves as your all‑in‑one creative hub by bringing every essential AI tool together to simplify your daily content creation. You can quickly turn ideas, photos, and scripts into viral reels from a single dashboard.',
  ctaText: 'Create Reels Now',
};

export const EXAMPLE_VIDEOS = [
  {
    id: 'video-1',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-facemask.mp4',
    posterUrl: '',
    alt: 'AI Reel Example 1',
  },
  {
    id: 'video-2',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-earbuds.mp4',
    posterUrl: '',
    alt: 'AI Reel Example 2',
  },
  {
    id: 'video-3',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-outfit.mp4',
    posterUrl: '',
    alt: 'AI Reel Example 3',
  },
  {
    id: 'video-4',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-cream.mp4',
    posterUrl: '',
    alt: 'AI Reel Example 4',
  },
  {
    id: 'video-5',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-perfume.mp4',
    posterUrl: '',
    alt: 'AI Reel Example 5',
  },
  {
    id: 'video-6',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-energy-drink.mp4',
    posterUrl: '',
    alt: 'AI Reel Example 6',
  },
  {
    id: 'video-7',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-product-review-reaction.mp4',
    posterUrl: '',
    alt: 'AI Reel Example 7',
  },
];

/** Use Cases Section 配置 */
export const USE_CASES_CONFIG = {
  title: 'Infinite Creative Possibilities for Every Channel',
  subtitle:
    "Scale your brand's reach by deploying a diverse range of reels tailored for every stage of the customer journey. Our platform simplifies high-volume marketing by allowing you to generate platform-ready ads and organic content instantly.",
};

export const USE_CASES_TABS: ScaleTabData[] = [
  {
    key: 'social-media-ads',
    label: 'Social Media Ads',
    title: 'Social Media Ads',
    description:
      'Launch dozens of high-energy promotional clips to find your next winning creative without the cost of a full production team. FalcoCut allows you to test different hooks and visual styles across Instagram and TikTok to prevent audience fatigue and keep your ROAS high.',
    imageUrl: 'https://assets.falcocut.ai/ai-reel-generator/social-media-ads.png',
    ctaText: 'Create Video Ads Now',
    ctaUrl: '/ai-video-generator',
  },
  {
    key: 'product-showcases',
    label: 'Product Showcases',
    title: 'Product Showcases',
    description:
      'Transform product photos into dynamic 9:16 videos with realistic AI avatars that show your items being used in premium, real-world settings. FalcoCut places your products into aesthetic environments, creating the "lifestyle" feel that high-end shoppers expect from modern brands.',
    imageUrl: 'https://assets.falcocut.ai/ai-reel-generator/product-showcases.png',
    ctaText: 'Try Product Avatar',
    ctaUrl: '/ai-product-avatar',
  },
  {
    key: 'behind-the-scenes-snippets',
    label: 'Behind-the-Scenes Snippets',
    title: 'Behind-the-Scenes Snippets',
    description:
      'Give your audience a "peek behind the curtain" by generating realistic office or warehouse scenes that humanize your brand. Even if you work from a home office, you can use AI to create a professional brand environment that makes your company look established and trustworthy.',
    imageUrl:
      'https://assets.falcocut.ai/ai-reel-generator/behind-the-scenes-snippets.png',
    ctaText: 'Edit Your Video',
    ctaUrl: '/ai-video-editor',
  },
];

/** Why Choose Section 配置 */
export const WHY_CHOOSE_CONFIG = {
  title: 'Why Choose FalcoCut AI Reel Generator',
  subtitle:
    "Stop letting production bottlenecks hold back your brand's growth. FalcoCut gives you the professional tools to transform simple ideas into high-performing video assets that resonate with any digital audience.",
  ctaText: 'Create Reels Now',
};

export const WHY_CHOOSE_REASONS: ReasonItem[] = [
  {
    title: 'Instant Reel Generation',
    description:
      'Move from a creative concept to a finished export faster than ever before. FalcoCut handles the hard work of building your clips, so you can post new content every day without the stress of manual filming.',
  },
  {
    title: 'Built for Creative Control',
    description:
      'You stay in charge of how your brand looks and feels. Easily adjust the visual style, fine-tune the pacing, and direct exactly how your brand is presented to ensure every frame meets your standards.',
  },
  {
    title: 'Grow All Over the World',
    description:
      'Expand your reach globally without hiring a translation team or extra editors. Our AI instantly optimizes your content for every major social platform and translates your message into 30+ languages to engage audiences worldwide.',
  },
  {
    title: 'Save Your Marketing Budget',
    description:
      'Stop paying for expensive camera crews, studio rentals, and high-end gear. FalcoCut provides a full-service production suite for a small fraction of the cost, significantly increasing your marketing ROI.',
  },
  {
    title: 'One Tool for Every Goal',
    description:
      'Whether you need a quick sales ad, a helpful tutorial, or a fun social media post, our generator does it all. It is the only tool you need to create any type of short-form video.',
  },
  {
    title: 'A Professional Look Every Time',
    description:
      'Keep your brand looking clean and polished in every single post. By removing the "off-days" of traditional filming, FalcoCut ensures your videos are always high-quality and build real trust with your audience.',
  },
];

/** FAQ Section 配置 */
export const FAQ_CONFIG = {
  title: 'Frequently Asked Questions',
};

export const FAQ_LIST: FAQItem[] = [
  {
    question: 'What is an AI reel generator?',
    answer:
      'An AI reel generator is a tool that transforms ideas, scripts, or media into short vertical videos using automated editing and intelligent scene creation. It analyzes your input and assembles a complete video with pacing, structure, and platform‑ready formatting. This helps creators produce more content in less time without needing advanced editing skills. FalcoCut focuses on speed, simplicity, and creative flexibility so anyone can turn ideas into polished reels quickly.',
  },
  {
    question: 'How long does it take to generate a reel using AI?',
    answer:
      'Most AI‑generated reels are created within a few minutes, depending on the length and complexity of your input. The system processes your prompt, selects relevant elements, and assembles a coherent video automatically. This dramatically reduces the time normally spent on editing, trimming, and arranging clips manually. FalcoCut is designed to deliver fast results so you can create more content without slowing down your workflow.',
  },
  {
    question: 'Can I use my product photos to create reels?',
    answer:
      "Yes. You can upload product photos and instantly turn them into dynamic, motion‑driven clips using FalcoCut's image-to-video tools. Our system adds movement, transitions, and structure to make static images feel more engaging. This allows small businesses to showcase products without needing professional photography or video shoots. It's an efficient way to create polished promotional content from simple assets.",
  },
  {
    question: 'Can I localize reels for other countries?',
    answer:
      'Yes. FalcoCut allows you to generate localized versions of your reels by adjusting language, voiceover, and on‑screen text. This helps you adapt your content for different regions while keeping the same creative direction. Localization ensures your message feels natural and culturally relevant to each audience. It also makes it easier to scale campaigns across multiple markets without recreating videos from scratch.',
  },
  {
    question: 'How do I make reels look more professional?',
    answer:
      'Professional‑looking reels rely on clean pacing, consistent styling, and strong visual flow, all of which FalcoCut helps automate. The system enhances your footage with smooth transitions, balanced timing, and platform‑optimized framing. You can also refine colors, adjust motion, and apply templates to elevate the final look. These tools help your content appear intentional and polished even without editing experience.',
  },
  {
    question: 'Is FalcoCut AI reel generator suitable for paid social ads?',
    answer:
      'Yes. FalcoCut produces high‑quality vertical reels that meet the technical requirements of major advertising platforms. Our AI tool supports platform‑specific formats, making it easier to export content optimized for performance. You can also generate multiple variations to test different hooks, styles, or messages. This makes FalcoCut a strong fit for advertisers who need fast, consistent creative output.',
  },
  {
    question: 'Can I create UGC‑style reels for my business?',
    answer:
      'Yes. FalcoCut can generate UGC‑style content by combining natural‑sounding AI voices, casual pacing, and authentic visual styles. These elements help your videos feel more relatable and less like traditional ads. You can also incorporate your own clips or photos to add a personal touch. This approach allows brands to produce trust‑building content without hiring influencers or filming new footage.',
  },
  {
    question: 'How to make a reel on Instagram?',
    orderedList: [
      {
        title: 'Step 1:',
        content:
          'Open Instagram and tap the "+" button to start a new post, then choose the Reels option from the menu.',
      },
      {
        title: 'Step 2:',
        content:
          'Upload your clips or record directly in the app, arranging them in the order you prefer.',
      },
      {
        title: 'Step 3:',
        content:
          'Add music, text, effects, or timing adjustments to refine the flow and enhance the message.',
      },
      {
        title: 'Step 4:',
        content:
          'Write a caption, choose your cover image, and publish the reel to share it with your audience.',
      },
    ],
  },
  {
    question: 'Can I promote a small business on Instagram without showing my face?',
    answer:
      "Yes. You can promote your business effectively without appearing on camera by using product‑focused clips, text‑driven storytelling, or AI‑generated avatars. FalcoCut's AI Avatar video tool allows you to create spokesperson‑style content without recording yourself. This helps you maintain a consistent presence while keeping your personal identity private. It's a practical option for founders who prefer to stay behind the scenes while still building brand visibility.",
  },
];

/** Explore Tools Section 配置 */
export interface ExploreToolTag {
  title: string;
  link: string;
}

export const EXPLORE_TOOLS: ExploreToolTag[] = [
  { title: 'AI Video Generator', link: videoGenerateURL },
  { title: 'Product Avatar', link: productAvatarURL },
  { title: 'AI Video Editor', link: aiVideoEditURL },
  { title: 'AI Video Translator', link: videoTranslateURL },
  { title: 'Voice Clone', link: voiceCloneURL },
  { title: 'Facebook Video Ad Creator', link: facebookVideoAdURL },
  { title: 'AI UGC Video Generator', link: aiUgcVideoURL },
  { title: 'AI TikTok Video Generator', link: aiTiktokVideoURL },
];

/** Footer CTA Section 配置 */
export const FOOTER_CTA_CONFIG = {
  title: 'Create the Best Facebook Video Ads with AI',
  subtitle:
    'Ready to upgrade your video ad creation? Use FalcoCut to streamline production, reduce expenses, and maximize ROI across your Facebook marketing campaigns.',
  ctaText: 'Create Facebook Video Ads Now',
};

/** SEO 配置 */
export const SEO_CONFIG: SEOHeadProps = {
  title: PAGE_TITLE,
  description: PAGE_DESCRIPTION,
  url: PAGE_URL,
  publishedTime: '2026-03-24',
  modifiedTime: '2026-03-24',
  structuredData: [
    createSoftwareAppSchema({
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
    }),
    createWebPageSchema({
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      datePublished: '2026-03-24',
      dateModified: '2026-03-24',
    }),
    createFAQSchema({
      faqs: FAQ_LIST.map((f) => ({
        question: f.question,
        answer: f.answer || '',
      })),
    }),
    createBreadcrumbSchema({
      items: [
        { name: 'Home', url: 'https://falcocut.ai' },
        { name: 'AI Reel Generator', url: PAGE_URL },
      ],
    }),
    createHowToSchema({
      name: HOW_TO_CONFIG.title,
      steps: HOW_TO_STEPS.map((s) => ({ name: s.title, text: s.description })),
    }),
  ],
};
